<script setup>
import { computed, reactive, ref } from "vue"
import { translationFieldState, translationProgress } from "../utils/translation_field"
import JsonFieldInput from "./JsonFieldInput.vue"

const props = defineProps({
  formAction: { type: String, required: true },
  cancelUrl: { type: String, required: true },
  csrfToken: { type: String, required: true },
  sourceName: { type: String, default: "" },
  sourceDescription: { type: String, default: "" },
  sourceType: { type: String, required: true },
  translationStatus: { type: String, required: true },
  translationPersisted: { type: Boolean, default: false },
  name: { type: String, default: "" },
  status: { type: String, default: "draft" },
  fields: { type: Array, default: () => [] },
  showDescriptionNote: { type: Boolean, default: false },
  labels: { type: Object, default: () => ({}) },
})

const label = (key, fallback) => props.labels[key] || fallback

const translationName = ref(props.name)
const selectedStatus = ref(props.status)
const fieldValues = reactive(
  props.fields.reduce((acc, field) => {
    acc[field.key] = field.value ?? ""
    return acc
  }, {})
)
const jsonValidity = reactive(
  props.fields.reduce((acc, field) => {
    if (field.type === "json") acc[field.key] = true
    return acc
  }, {})
)

const progress = computed(() =>
  translationProgress(translationName.value, props.fields, fieldValues)
)

const hasInvalidJson = computed(() =>
  Object.values(jsonValidity).some((valid) => valid === false)
)

function sourceFieldClass(field) {
  return `form-group field-${translationFieldState(field.sourceValue, fieldValues[field.key])}`
}

function translationFieldClass(field) {
  return [
    "form-group",
    `field-${translationFieldState(field.sourceValue, fieldValues[field.key])}`,
  ]
}

function onJsonValidityChange(key, valid) {
  jsonValidity[key] = valid
}

function onSubmit(event) {
  if (hasInvalidJson.value) {
    event.preventDefault()
    alert(label("invalidJson", "Please fix invalid JSON fields before saving."))
  }
}
</script>

<template>
  <form
    :action="formAction"
    method="post"
    class="translation-edit-form"
    @submit="onSubmit"
  >
    <input type="hidden" name="authenticity_token" :value="csrfToken">
    <input type="hidden" name="_method" value="patch">

    <div class="translation-progress">
      <div class="translation-progress-bar">
        <div class="translation-progress-fill" :style="{ width: `${progress.percent}%` }" />
      </div>
      <span class="translation-progress-label">
        {{ label("progress", "Translation progress") }}:
        {{ progress.completed }} / {{ progress.total }}
        ({{ progress.percent }}%)
      </span>
    </div>

    <div class="translation-editor">
      <div class="editor-panel source-panel">
        <div class="panel-header">
          <h2>{{ label("sourceTitle", "German (Source)") }}</h2>
          <span class="badge badge-info">{{ label("readonly", "Read-only") }}</span>
        </div>
        <div class="panel-content">
          <div class="form-group">
            <label>{{ label("name", "Name") }}</label>
            <input type="text" class="form-control" :value="sourceName" disabled>
          </div>

          <div v-if="showDescriptionNote" class="form-group">
            <label>{{ label("description", "Description") }}</label>
            <textarea class="form-control" rows="4" disabled>{{ sourceDescription }}</textarea>
          </div>

          <div
            v-for="field in fields"
            :key="`source-${field.key}`"
            :class="sourceFieldClass(field)"
          >
            <label>{{ field.label }}</label>
            <textarea
              v-if="field.type === 'textarea' || field.type === 'json'"
              class="form-control"
              rows="3"
              disabled
            >{{ field.sourceValue }}</textarea>
            <input
              v-else
              type="text"
              class="form-control"
              :value="field.sourceValue"
              disabled
            >
          </div>
        </div>
      </div>

      <div class="editor-panel translation-panel">
        <div class="panel-header">
          <h2>{{ label("translationTitle", "English (Translation)") }}</h2>
          <span class="badge badge-type">{{ sourceType }}</span>
          <span class="badge" :class="`badge-${translationPersisted ? translationStatus : 'draft'}`">
            {{ translationPersisted ? translationStatus : label("statusNew", "New") }}
          </span>
        </div>
        <div class="panel-content">
          <div class="form-group field-translated">
            <label for="content_name">{{ label("name", "Name") }} *</label>
            <input
              id="content_name"
              v-model="translationName"
              type="text"
              name="content[name]"
              class="form-control"
              required
            >
          </div>

          <div v-if="showDescriptionNote" class="form-group">
            <label>{{ label("description", "Description") }}</label>
            <p class="form-help">
              {{ label("descriptionNote", "Descriptions are managed per-book.") }}
            </p>
          </div>

          <div
            v-for="field in fields"
            :key="`translation-${field.key}`"
            :class="translationFieldClass(field)"
          >
            <label :for="`content_data_${field.key}`">{{ field.label }}</label>

            <JsonFieldInput
              v-if="field.type === 'json'"
              :input-id="`content_data_${field.key}`"
              :input-name="`content[data][${field.key}]`"
              :model-value="fieldValues[field.key]"
              :rows="field.rows || 4"
              @update:model-value="fieldValues[field.key] = $event"
              @validity-change="onJsonValidityChange(field.key, $event)"
            />

            <textarea
              v-else-if="field.type === 'textarea'"
              :id="`content_data_${field.key}`"
              v-model="fieldValues[field.key]"
              :name="`content[data][${field.key}]`"
              class="form-control"
              rows="3"
            />

            <input
              v-else
              :id="`content_data_${field.key}`"
              v-model="fieldValues[field.key]"
              :name="`content[data][${field.key}]`"
              type="text"
              class="form-control"
            >
          </div>

          <div class="form-group">
            <label for="content_status">{{ label("status", "Status") }}</label>
            <select id="content_status" v-model="selectedStatus" name="content[status]" class="form-control">
              <option value="draft">
                {{ label("statusDraft", "Draft") }}
              </option>
              <option value="pending_review">
                {{ label("statusPendingReview", "Pending Review") }}
              </option>
              <option value="published">
                {{ label("statusPublished", "Published") }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="form-actions">
      <a :href="cancelUrl" class="btn btn-secondary">{{ label("cancel", "Cancel") }}</a>
      <button type="submit" class="btn btn-primary">
        <i class="fa-solid fa-floppy-disk" />
        {{ label("save", "Save Translation") }}
      </button>
      <button type="submit" name="content[status]" value="pending_review" class="btn btn-warning">
        {{ label("saveAndSubmit", "Save & Submit for Review") }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.translation-progress {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.translation-progress-bar {
  height: 0.5rem;
  background: var(--bg-dark);
  border-radius: 999px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.translation-progress-fill {
  height: 100%;
  background: var(--color-primary, #0d6efd);
  transition: width 0.2s ease;
}

.translation-progress-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.translation-editor {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;
}

.editor-panel {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
}

.panel-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--bg-dark);
}

.panel-header h2 {
  margin: 0;
  font-size: 1rem;
  flex: 1;
}

.panel-content {
  padding: 1rem;
  max-height: 600px;
  overflow-y: auto;
}

.source-panel .form-control:disabled {
  background: var(--bg-dark);
  color: var(--text-secondary);
  cursor: not-allowed;
}

.form-actions {
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.form-help {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-style: italic;
  margin: 0;
}

.json-field {
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.85rem;
}

.json-field-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.json-status {
  font-size: 0.8rem;
}

.json-status-valid {
  color: #198754;
}

.json-status-empty {
  color: var(--text-secondary);
}

.json-status-invalid {
  color: #dc3545;
}

.field-empty .form-control {
  border-color: #ffc107;
}

.field-unchanged .form-control {
  border-color: var(--border-color);
}

.field-translated .form-control {
  border-color: #198754;
}

@media (max-width: 1024px) {
  .translation-editor {
    grid-template-columns: 1fr;
  }
}
</style>
